/**
 * useScrollToSection - Hook for scroll-to-section navigation
 *
 * Manages section refs and smooth scrolling for QuickNav component.
 * Uses ScrollView.scrollTo with animated behavior.
 *
 * Phase 3.2: QuickNav Component
 */

import React, { useRef, useCallback, useMemo } from 'react';
import { View, ScrollView, Platform, LayoutRectangle } from 'react-native';

/** Available section types for navigation */
export type SectionId =
  | 'score'
  | 'summary'
  | 'issues'
  | 'suggestions'
  | 'patterns'
  | 'transformations'
  | 'feedback';

/** Section layout data */
interface SectionLayout {
  y: number;
  height: number;
}

/** Options for scroll behavior */
interface ScrollToSectionOptions {
  /** Offset from top to account for sticky nav (default: 60) */
  topOffset?: number;
  /** Whether to animate scroll (default: true) */
  animated?: boolean;
}

/** Return type for the hook */
interface UseScrollToSectionReturn {
  /** Ref for the ScrollView container */
  scrollViewRef: React.RefObject<ScrollView | null>;
  /** Register a section's layout when measured */
  registerSection: (sectionId: SectionId, layout: LayoutRectangle) => void;
  /** Scroll to a specific section */
  scrollToSection: (sectionId: SectionId, options?: ScrollToSectionOptions) => void;
  /** Get available sections (sections that have been registered) */
  getAvailableSections: () => SectionId[];
}

/**
 * Custom hook for managing scroll-to-section navigation
 *
 * @example
 * ```tsx
 * const { scrollViewRef, registerSection, scrollToSection } = useScrollToSection();
 *
 * // In ScrollView
 * <ScrollView ref={scrollViewRef}>
 *   <View onLayout={(e) => registerSection('issues', e.nativeEvent.layout)}>
 *     ...
 *   </View>
 * </ScrollView>
 *
 * // In QuickNav chip
 * <Pressable onPress={() => scrollToSection('issues')}>
 * ```
 */
export function useScrollToSection(): UseScrollToSectionReturn {
  const scrollViewRef = useRef<ScrollView>(null);
  const sectionLayouts = useRef<Map<SectionId, SectionLayout>>(new Map());

  /**
   * Register a section's layout when it's measured
   */
  const registerSection = useCallback((sectionId: SectionId, layout: LayoutRectangle) => {
    sectionLayouts.current.set(sectionId, {
      y: layout.y,
      height: layout.height,
    });
  }, []);

  /**
   * Scroll to a specific section with smooth animation
   */
  const scrollToSection = useCallback(
    (sectionId: SectionId, options: ScrollToSectionOptions = {}) => {
      const { topOffset = 60, animated = true } = options;

      const layout = sectionLayouts.current.get(sectionId);
      if (!layout) {
        console.warn(`[useScrollToSection] Section not registered: ${sectionId}`);
        return;
      }

      if (!scrollViewRef.current) {
        console.warn('[useScrollToSection] ScrollView ref not available');
        return;
      }

      // Calculate scroll position with offset for sticky nav
      const scrollY = Math.max(0, layout.y - topOffset);

      // Web uses scrollTo, native uses scrollTo with y parameter
      if (Platform.OS === 'web') {
        // Web behavior - use scrollTo with behavior smooth
        scrollViewRef.current.scrollTo({
          y: scrollY,
          animated,
        });
      } else {
        // Native behavior
        scrollViewRef.current.scrollTo({
          y: scrollY,
          animated,
        });
      }
    },
    []
  );

  /**
   * Get list of sections that have been registered
   */
  const getAvailableSections = useCallback((): SectionId[] => {
    return Array.from(sectionLayouts.current.keys());
  }, []);

  return useMemo(
    () => ({
      scrollViewRef,
      registerSection,
      scrollToSection,
      getAvailableSections,
    }),
    [registerSection, scrollToSection, getAvailableSections]
  );
}

/**
 * Default top offset to account for sticky QuickNav
 * Based on QuickNav height (48px) + padding (12px)
 */
export const QUICKNAV_TOP_OFFSET = 60;
